import React from 'react';
import GlobalConfig from '../../../types/report/GlobalModuleConfig';
import ActionType from '../../../types/report/ActionType';
import ComponentType from '../../../types/report/ComponentType';
import IdNameAndNumber from '../../../types/report/IdNameAndNumber';
import InsertAtPositionModal from '../../Common/Modals/InsertAtPositionModal/InsertAtPositionModal';
import reportComponentBuildersHelper from '../Common/ReportComponentsHelper';
import ReportConfig from '../../../types/report/ReportConfig';
import SelectSearch, { SelectSearchOption, SelectedOptionValue } from 'react-select-search';
import RadioButtonGroup from '../../Common/RadioButtonGroup/RadioButtonGroup';
import { reportType } from '../../../infrastructure/constants/reportType';
import './styles.scss';
import Mode from '../../../types/report/Mode';
import SelectOption from '../../../infrastructure/types/SelectOption';
import IdAndType from '../../../types/report/IdAndType';
import { validatePosition } from './GlobalModule.Helpers';

interface Props {
    reportConfig: ReportConfig;
    onConfiguredInPopup: (name: string, type: string, isInsertAfter: boolean, selectedNumber: string, selectedGlobalId: number) => void;
    dropdownOptions?: SelectSearchOption[];
    getGlobalOptions: (dataType: string) => SelectSearchOption[];
    onClose: () => void;
    title: string;
    dropdownTypeName: string;
    componentType: ComponentType;
    components: IdNameAndNumber[];
    dataTypeOtions?: SelectOption[];
    refreshGlobalOptionsOnDataTypeChange?: boolean;
    onGlobalOptionChange?: (id: number | null) => void;
    isInsertAtpositionDisabled?: boolean;
    globalCollection: IdAndType[];
    defaultDropdownOption?: string;
    globalConfig: GlobalConfig;
}

interface State {
    mode: Mode;
    error: string;
    selectedDataType: string;
    selectedGlobalComponentId: number | null;
}


const local: SelectOption = {
    text: 'Local',
    value: 'Local'
};

const global: SelectOption = {
    text: 'Global',
    value: 'Global'
};


const noGlobalComponentSelectedError = 'Please select global component.';

class AddComponentWithGlobalOptionsPopup extends React.Component<Props, State> {

    private options: SelectSearchOption[] = [];

    constructor(props: Props) {
        super(props);
        this.state = {
            mode: Mode.Local,
            error: '',
            selectedDataType: this.props.defaultDropdownOption || this.props.dataTypeOtions?.[0]?.text || '',
            selectedGlobalComponentId: 0,
        }

        this.onGlobalOptionChange = this.onGlobalOptionChange.bind(this);
        this.validateInsertBeforeAfter = this.validateInsertBeforeAfter.bind(this);
        this.onModeChange = this.onModeChange.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onDataTypeChange = this.onDataTypeChange.bind(this);
        this.getModeOptions = this.getModeOptions.bind(this);
    }

    getModeOptions(): SelectOption[] {
        if (this.props.reportConfig.type === reportType.accuracy) {
            return [local];
        }

        return [
            local,
            global
        ];
    }

    async validateInsertBeforeAfter(number: number, isInsertAfter: boolean) {
        return await reportComponentBuildersHelper.validateAction(
            number,
            isInsertAfter,
            this.props.components,
            this.props.componentType,
            ActionType.Add,
            this.props.reportConfig);
    }

    validateGlobalId(id: number) {
        return id > 0;
    }

    onGlobalOptionChange(value: SelectedOptionValue | SelectedOptionValue[]): void {

        this.props.onGlobalOptionChange && this.props.onGlobalOptionChange(parseInt(value.toString()));
        this.setState({ error: '', selectedGlobalComponentId: parseInt(value.toString()) });
    }

    onModeChange(value: string): void {

        let mode = value === Mode.Local ? Mode.Local : Mode.Global;
        let globalId = value === Mode.Local ? null : this.state.selectedGlobalComponentId || (this.options?.[0]?.value ? parseInt(this.options?.[0]?.value) as number : null);

        this.setState({ mode, error: '', selectedGlobalComponentId: globalId });

        let type = this.props.globalCollection.find(x => x.id === globalId)?.type;

        if (type) {
            this.setState({ selectedDataType: type, mode });
        }

        this.props.onGlobalOptionChange && this.props.onGlobalOptionChange(globalId);
    }

    onSave(name: string, type: string, isInsertAfter: boolean, selectedNumber: string) {

        let globalId = this.state.mode !== Mode.Global ? 0 : this.state.selectedGlobalComponentId && this.options.length ?
            parseInt(this.options.find(x => x.value === (this.state.selectedGlobalComponentId || 0).toString())?.value || '0') :
            this.options.length ? parseInt(this.options[0]?.value) : 0;

        let insertionNumberInt = parseInt(selectedNumber);

        let position = isInsertAfter ? insertionNumberInt : insertionNumberInt - 1;

        if (globalId > 0) {
            let error = validatePosition(this.props.globalConfig, this.props.reportConfig, position, globalId, this.props.componentType);

            if (error) {
                this.setState({ error });

                return;
            }
        }

        if (this.state.mode === Mode.Global && !globalId) {
            this.setState({ error: noGlobalComponentSelectedError });
        }
        else {
            this.props.onConfiguredInPopup(name, type, isInsertAfter, selectedNumber, globalId);
        }
    }

    getModeHtml(): JSX.Element {

        return <>
            <span className='font-weight-bold margin-bottom-0'>Mode</span>
            <div className='height-40-px display-flex labels-display-inline-flex display-flex width-100-percents'>
                <div className='width-30-percents display-flex align-items-center min-width-160px'>
                    <RadioButtonGroup
                        options={this.getModeOptions()}
                        value={this.state.mode}
                        name='isGlobal'
                        onChange={this.onModeChange}
                        labelClassName='display-inline-flex font-robotoBold'
                    />
                </div>
                {this.props.reportConfig.type !== reportType.accuracy && this.state.mode === Mode.Global &&
                    <div className='width-70-percents'>
                        <SelectSearch
                            options={this.options}
                            onChange={this.onGlobalOptionChange}
                            search={true}
                            value={this.props.refreshGlobalOptionsOnDataTypeChange ? (this.state.selectedGlobalComponentId || 0).toString() : this.options?.[0]?.value || ''}
                        />
                    </div>
                }
            </div>
        </>
    }

    onDataTypeChange(dataType: string) {

        let state = {
            selectedDataType: dataType,
            error: '',
            mode: this.state.mode,
            selectedGlobalComponentId: this.props.refreshGlobalOptionsOnDataTypeChange ? 0 : this.state.selectedGlobalComponentId
        };

        this.setState(state);
    }

    setOptions() {
        this.options = this.props.getGlobalOptions(this.state.selectedDataType);
    }

    render(): JSX.Element {

        this.setOptions();

        return (
            <InsertAtPositionModal
                error={this.state.error}
                component={this.props.reportConfig.type === reportType.globalModule ? null : this.getModeHtml()}
                title={this.props.title}
                onSaveClick={this.onSave}
                name={this.props.componentType}
                dropdownOptions={this.props.dropdownOptions}
                dropdownForTypeName={this.props.dropdownTypeName}
                onClose={() => this.props.onClose()}
                dataTypeOptions={this.props.dataTypeOtions}
                defaultDropdownOption={this.props.defaultDropdownOption ? this.props.dropdownOptions?.find(x => x.name === this.props.defaultDropdownOption) : this.props.dropdownOptions?.[0]}
                defaultDropdownOptionAlwaysLastOne={true}
                validateFunction={this.validateInsertBeforeAfter}
                onChangeDataType={this.onDataTypeChange}
                defaultDataType={this.state.selectedDataType}
                isDataTypeSelectionDisabled={this.state.mode === Mode.Global}
                isInsertAtpositionDisabled={this.props.isInsertAtpositionDisabled}
                onInsertComponentChange={() => this.setState({ error: '' })}
                showNameAndType={this.state.mode === Mode.Local}
            />
        )
    }
}

export default AddComponentWithGlobalOptionsPopup;